import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { webhookClient } from '../../../services/webhookClient';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Snackbar,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  CustomSortableTable,
  DashboardLayout,
  SortableTableAction,
  SortableTableHeader,
  SortableTableRow,
} from '@blumtechgroup/blum-react-core-components';

interface Questionnaire {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  send_to: string;
}

const GET_ALL_QUESTIONNAIRES = gql`
  query GetAllQuestionnaires {
    dans_fund_questionnaire {
      id
      name
      created_at
      updated_at
      send_to
    }
  }
`;

const GET_CLINICIANS = gql`
  query GetClinicians {
    users_clinician(limit: 1) {
      id
    }
  }
`;

// GraphQL mutation to delete a questionnaire
const DELETE_QUESTIONNAIRE = gql`
  mutation DeleteQuestionnaire($id: uuid!) {
    delete_dans_fund_questionnaire_by_pk(id: $id) {
      id
    }
  }
`;

const CREATE_PATIENT = gql`
  mutation CreatePatient($phoneNumber: String!, $clinicianId: uuid!) {
    insert_dans_fund_patients_one(object: {
      phone_number: $phoneNumber,
      clinician_id: $clinicianId
    }) {
      id
      phone_number
    }
  }
`;

const AdminDansFundQuestionnaire = (): React.ReactElement => {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [questionnaireToDelete, setQuestionnaireToDelete] = useState<string | null>(null);

  // SMS Modal state
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const { data, loading, error } = useQuery(GET_ALL_QUESTIONNAIRES, {
    fetchPolicy: 'no-cache',
  });

  // Get the first clinician's ID
  const { data: clinicianData, loading: clinicianLoading } = useQuery(GET_CLINICIANS, {
    fetchPolicy: 'cache-first',
  });

  const defaultClinicianId = clinicianData?.users_clinician?.[0]?.id || '00000000-0000-0000-0000-000000000000';

  const [deleteQuestionnaire, { loading: deleteLoading }] = useMutation(DELETE_QUESTIONNAIRE, {
    refetchQueries: [{ query: GET_ALL_QUESTIONNAIRES }],
    onCompleted: () => {
      setDeleteDialogOpen(false);
      setQuestionnaireToDelete(null);
    },
  });

  const [createPatient] = useMutation(CREATE_PATIENT);

  const handleView = (id: string) => {
    navigate(`/admin/dans-fund-questionnaire/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/admin/dans-fund-questionnaire/${id}/edit`);
  };

  const handleCreate = () => {
    navigate('/admin/dans-fund-questionnaire/create');
  };

  const handleDeleteClick = (id: string) => {
    setQuestionnaireToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (questionnaireToDelete) {
      deleteQuestionnaire({
        variables: {
          id: questionnaireToDelete,
        },
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setQuestionnaireToDelete(null);
  };

  const handleSendSmsClick = () => {
    setSmsDialogOpen(true);
  };

  const handleSmsDialogClose = () => {
    setSmsDialogOpen(false);
    setPhoneNumber('');
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
  };

  const handleSendSms = async () => {
    if (!phoneNumber) return;

    setSendingSms(true);

    try {
      // First, create a patient record
      // Use the first clinician's ID, or fallback to default if none found
      const clinicianId = defaultClinicianId;

      const patientResult = await createPatient({
        variables: {
          phoneNumber,
          clinicianId
        }
      });

      const patientId = patientResult.data?.insert_dans_fund_patients_one?.id;

      if (!patientId) {
        throw new Error('Failed to create patient record');
      }

      // Then send the SMS with the patient ID
      const result = await webhookClient.postAnon(
        'dans-fund/send-test-sms',
        JSON.stringify({
          phoneNumber,
          patientId
        })
      );

      if (result.success) {
        setSnackbarMessage('SMS sent successfully!');
        setSnackbarSeverity('success');
        handleSmsDialogClose();
      } else {
        setSnackbarMessage(`Failed to send SMS: ${result.message}`);
        setSnackbarSeverity('error');
      }
    } catch (error: any) {
      console.error('Error in handleSendSms:', error);
      setSnackbarMessage(`Error: ${error?.message || 'Failed to send test SMS'}`);
      setSnackbarSeverity('error');
    } finally {
      setSendingSms(false);
      setSnackbarOpen(true);
    }
  };

  if (loading) return <div>Loading questionnaires...</div>;
  if (error) return <div>Error loading questionnaires</div>;

  const questionnaires = data?.dans_fund_questionnaire || [];

  const rows: SortableTableRow[] = questionnaires.map((questionnaire: Questionnaire) => ({
    id: questionnaire.id,
    actions: [
      {
        label: 'View',
        onClick: () => handleView(questionnaire.id),
      },
      {
        label: 'Edit',
        onClick: () => handleEdit(questionnaire.id),
      },
      {
        label: 'Delete',
        onClick: () => handleDeleteClick(questionnaire.id),
        color: 'error',
        icon: <DeleteIcon />,
      },
    ],
    columns: [
      {
        key: 'name',
        label: questionnaire.name,
      },
      {
        key: 'created_at',
        label: new Date(questionnaire.created_at).toLocaleDateString(),
      },
      {
        key: 'updated_at',
        label: new Date(questionnaire.updated_at).toLocaleDateString(),
      },
      {
        key: 'send_to',
        label: questionnaire.send_to,
      },
    ],
  }));

  const actions: SortableTableAction[] = [
    {
      key: 'create',
      label: 'Create',
      icon: <AddIcon />,
      onClick: handleCreate,
    },
    {
      key: 'sendSms',
      label: 'Send SMS',
      icon: <SmsIcon />,
      onClick: handleSendSmsClick,
    },
  ];

  const headers: SortableTableHeader[] = [
    {
      key: 'name',
      label: 'Name',
    },
    {
      key: 'created_at',
      label: 'Created At',
    },
    {
      key: 'updated_at',
      label: 'Updated At',
    },
    {
      key: 'send_to',
      label: 'Send To',
    }
  ];

  return (
    <DashboardLayout title="Dan's Fund Questionnaire Management">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <CustomSortableTable
            title="Dan's Fund Questionnaires"
            counter={data?.dans_fund_questionnaire ? 1 : 0}
            headers={headers}
            rows={rows}
            actions={actions}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {'Delete Questionnaire'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this questionnaire? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            autoFocus
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send SMS Dialog */}
      <Dialog
        open={smsDialogOpen}
        onClose={handleSmsDialogClose}
        aria-labelledby="sms-dialog-title"
        aria-describedby="sms-dialog-description"
      >
        <DialogTitle id="sms-dialog-title">
          Send Test SMS
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="sms-dialog-description" sx={{ mb: 2 }}>
            Enter a phone number to send a test SMS message. This will help
            verify that Twilio is configured correctly.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="phone"
            label="Phone Number"
            type="tel"
            fullWidth
            variant="outlined"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            placeholder="+1234567890"
            helperText="Include country code (e.g., +1 for US)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSmsDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSendSms}
            color="primary"
            variant="contained"
            disabled={!phoneNumber || sendingSms}
          >
            {sendingSms ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default AdminDansFundQuestionnaire;
